import { Step } from "react-joyride";

export const WELCOME_TOUR: Step[] = [
  {
    target: "#tour-dashboard",
    content:
      "This is your dashboard. New and pending customer conversations appear here.",
    disableBeacon: true,
  },
  {
    target: "#tour-chats",
    content:
      "All messages from Facebook, Instagram, WhatsApp, and SMS are handled here.",
  },
  {
    target: "#tour-users",
    content: "Create managers and agents to manage customer conversations.",
  },
  {
    target: "#tour-reports",
    content:
      "View analytics and performance reports to track team activity and customer engagement.",
  },
  {
    target: "#tour-settings",
    content: "Connect your business channels and configure your workspace.",
  },
  {
    target: "#tour-settings", // Or maybe a modal? But user asked for steps. I will stick to last step on settings or center.
    content: "Let's connect your first channel to start receiving messages.",
    placement: "center", // Final step as a general CTA? Or pointing to settings again. The prompt says "Final CTA: ...". Center placement makes sense for a general call to action.
  },
];

export const POST_FB_CONNECTION_TOUR: Step[] = [
  {
    target: "#tour-connect-page-button",
    title: "Connect Your First Page",
    content:
      "First, click here to connect your page/number to start receiving messages.",
    disableBeacon: true,
    spotlightClicks: true,
    disableOverlay: true,
    placement: "auto",
  },
  {
    target: "#tour-users",
    title: "Configure Profile",
    content:
      "Excellent! Your channel is connected. Now, let's configure your profile to start receiving messages. Click the Users icon in the sidebar, or hit Finish to jump straight there.",
    spotlightClicks: true,
    placement: "auto",
  },
];

export const USER_MANAGEMENT_TOUR: Step[] = [
  {
    target: "#tour-users-add-dropdown",
    title: "Invite Users",
    content:
      "Start by inviting new agents to your platform. Click here to see the options.",
    disableBeacon: true,
  },
  {
    target: "#tour-users-invite-item",
    title: "Single Invitation",
    content:
      "Invite a single user by email. You can assign their role and team immediately.",
  },
  {
    target: "#tour-users-share-item",
    title: "Batch Invitation",
    content:
      "Generate a shareable link to invite multiple users quickly. Perfect for onboarding large teams.",
  },
  {
    target: "#tour-users-create-team-item",
    title: "Team Structure",
    content:
      "Create new teams to organize your agents. Assign supervisors and defining team responsibilities.",
  },
  {
    target: "#tour-view-own-profile",
    title: "Your Profile",
    content: "Click 'View Details' to see your profile.",
    placement: "auto",
    disableScrolling: true,
  },
  {
    target: "#tour-edit-button",
    title: "Profile Configuration",
    content: "Click 'Edit' to configure your profile.",
  },
  {
    target: "#tour-assign-pages-section",
    title: "Channel Assignment",
    content:
      "Select the Facebook page you just connected. Choose for replies, comments, or both.",
  },
  {
    target: "#tour-chat-limit-input",
    title: "Conversation Limits",
    content: "Set your chat limit (e.g., 10 active conversations).",
  },
  {
    target: "#tour-comment-limit-input",
    title: "Comment Management",
    content: "Set your comment limit (e.g., 5 posts to manage).",
  },
  {
    target: "#tour-online-toggle",
    title: "Go Online",
    content: "Toggle this ON when you're ready to receive customer messages!",
    placement: "bottom",
    disableScrolling: true,
  },
];

export const DASHBOARD_TOUR: Step[] = [
  {
    target: "#tour-dashboard-sales-card",
    title: "Sales Overview",
    content:
      "Track conversations marked as successful sales. This helps you measure conversion rates.",
    disableBeacon: true,
  },
  {
    target: "#tour-dashboard-active-card",
    title: "Active Conversations",
    content: "View your currently active conversations across all channels.",
  },
  {
    target: "#tour-dashboard-closed-card",
    title: "Resolved Chats",
    content: "See conversations that have been closed or resolved.",
  },
  {
    target: "#tour-dashboard-total-card",
    title: "Lifetime Volume",
    content: "Total number of conversations for the selected time period.",
  },
  {
    target: "#tour-dashboard-active-widgets",
    title: "Active Widgets",
    content:
      "Tracking the total number of currently active Chatbot widget sessions on your connected properties.",
  },
  {
    target: "#tour-dashboard-closed-bot-chats",
    title: "Closed Bot Chats",
    content:
      "Conversations that were successfully handled and resolved by the AI Chatbot without agent intervention.",
  },
  {
    target: "#tour-dashboard-bot-handoffs",
    title: "Bot Handoffs",
    content:
      "Sessions where the Chatbot seamlessly transferred the conversation to a human agent.",
  },
  {
    target: "#tour-dashboard-widget-customers",
    title: "Widget Customers",
    content:
      "The total unique number of customers who have interacted with your Chatbot widget.",
  },
  {
    target: "#tour-dashboard-pending-stats",
    title: "Pending Queue",
    content:
      "Pending conversations waiting to be assigned. Click on any channel segment to view details.",
  },
  {
    target: "#tour-dashboard-users",
    title: "Team Activity",
    content:
      "Monitor your team members' online status and activity in real-time.",
  },
  {
    target: "#tour-dashboard-graphical-chart",
    title: "Engagement Trends",
    content:
      "Visualize conversation trends over the last 7 days to identify patterns.",
  },
  {
    target: "#tour-dashboard-chat-statistics",
    title: "Daily Statistics",
    content:
      "Quick stats showing today, yesterday, and weekly conversation metrics.",
  },
  {
    target: "#tour-dashboard-pages-breakdown",
    title: "Channel Breakdown",
    content:
      "See which channels and pages are receiving the most conversations.",
  },
];

export const REPORTS_TOUR: Step[] = [
  {
    target: "#tour-reports-header",
    title: "Performance Metrics",
    content:
      "View the total count of conversations across all channels - Facebook, Instagram, and WhatsApp.",
    disableBeacon: true,
  },
  {
    target: "#tour-reports-customers-figure",
    title: "Customer Reach",
    content:
      "See the total number of unique customers who contacted you during the selected period.",
  },
  {
    target: "#tour-reports-graphical-summary",
    title: "Data Visualization",
    content:
      "Analyze conversation trends over time with interactive charts. Use the filters to select date ranges and specific team members.",
  },
  {
    target: "#tour-reports-pages-table",
    title: "Source Breakdown",
    content:
      "Breakdown of conversations by connected pages and channels. This helps identify which pages are receiving the most traffic.",
  },
  {
    target: "#tour-reports-tabular-data",
    title: "Detailed Log",
    content:
      "Detailed table view of all conversations with customer names, channels, labels, pages, and assigned agents. Perfect for in-depth analysis.",
  },
];

export const CHAT_SCREEN_TOUR: Step[] = [
  {
    target: "#tour-chat-channel-tabs",
    title: "Omnichannel Inbox",
    content:
      "Switch between different channels to view conversations from Facebook, WhatsApp, Instagram, or all channels at once. The badge shows unread message counts.",
    disableBeacon: true,
  },
  {
    target: "#tour-online-toggle",
    title: "Availability Status",
    content:
      "Toggle your availability status. When 'Available', you'll receive new conversations automatically. Set to 'Away' when you need a break.",
  },
  {
    target: "#tour-chat-conversation-list",
    title: "Active Chats",
    content:
      "All your assigned conversations appear here. Click on any conversation to view and respond to messages. Unread conversations are highlighted.",
  },
  {
    target: "#tour-chat-search",
    title: "Search & Filter",
    content:
      "Search for specific conversations by customer name or message content. Use filters to quickly find what you need.",
  },
  {
    target: "#tour-chat-message-area",
    title: "Message Timeline",
    content:
      "The conversation history appears here. Scroll up to see older messages. You can see customer messages and your team's responses.",
  },
  {
    target: "#tour-chat-message-input",
    title: "Reply Box",
    content:
      "Type your message here. Press Enter to send or Shift+Enter for a new line. Your message will be sent to the customer instantly.",
  },
  {
    target: "#tour-chat-emoji-button",
    title: "Emojis",
    content:
      "Add emojis to your messages to make conversations more friendly and engaging. Click to open the emoji picker.",
  },
  {
    target: "#tour-chat-text-enhancer",
    title: "AI Text Enhancer",
    content:
      "Make your messages clearer and more professional with a single click using our AI text enhancer.",
  },
  {
    target: "#tour-chat-text-translator",
    title: "AI Text Translator",
    content:
      "Need to reply in another language? Select your target language and let AI instantly translate your message.",
  },
  {
    target: "#tour-chat-voice-to-text",
    title: "AI Voice-to-Text",
    content:
      "Speak directly instead of typing! Click this icon to record, and our AI will transcribe it for you.",
  },
  {
    target: "#tour-chat-audio-recorder",
    title: "Record Voice Clip",
    content: "Send a recorded audio message directly to the customer.",
  },
  {
    target: "#tour-chat-attachment-button",
    title: "Attachments",
    content:
      "Send images, videos, or files to customers. Click here to upload media from your computer.",
  },
  {
    target: "#tour-chat-canned-responses",
    title: "Quick Replies",
    content:
      "Save time with pre-written responses! Click here to insert frequently used messages. You can create personal or team-wide canned responses.",
  },
  {
    target: "#tour-chat-transfer",
    title: "Transfer Chat",
    content:
      "Transfer this conversation to another agent or team member. Useful when someone else is better suited to handle the inquiry.",
  },
  {
    target: "#tour-chat-mark-sale",
    title: "Mark as Sale",
    content:
      "Mark this conversation as a successful sale. This helps track your conversion rate and sales performance.",
  },
  {
    target: "#tour-chat-ai-summarizer",
    title: "AI Conversation Summarizer",
    content:
      "Catching up? Click here to instantly generate a brief, comprehensive summary of the entire chat history so far.",
  },
  {
    target: "#tour-chat-search-within-chat",
    title: "Search Messages",
    content:
      "Easily search and find specific texts and keywords within the current conversation.",
  },
  {
    target: "#tour-chat-close",
    title: "Resolve Conversation",
    content:
      "Close the conversation when it's resolved. Closed conversations won't appear in your active list but remain searchable.",
  },
  {
    target: "#tour-chat-view-details",
    title: "Toggle Chat Details",
    content:
      "Open or close the side panel to view customer information, manage tags, and add internal notes.",
  },
  {
    target: "#tour-chat-customer-info",
    title: "Customer Profile",
    content:
      "View customer details including name, profile picture, and contact information. This helps you personalize your responses.",
  },
  {
    target: "#tour-chat-labels",
    title: "Smart Labels",
    content:
      "Organize conversations with labels like 'Important', 'Follow-up', or create custom labels. Labels help you categorize and prioritize chats.",
  },
  {
    target: "#tour-chat-ai-label-suggestion",
    title: "AI Label Assitant",
    content:
      "Not sure how to categorize this chat? Let the AI analyze the conversation and suggest the most appropriate labels for you.",
  },
  {
    target: "#tour-chat-notes",
    title: "Internal Notes",
    content:
      "Add internal notes about this conversation. Notes are only visible to your team, not customers. Perfect for sharing context with colleagues.",
  },
  {
    target: "#tour-chat-reminder",
    title: "Follow-up Reminders",
    content:
      "Set a reminder to follow up with this customer at a specific date and time. You'll receive a notification when it's time.",
  },
];

export const SETTINGS_TOUR: Step[] = [
  {
    target: "#tour-settings-channels",
    title: "Channel Management",
    content:
      "Connect and manage your social media channels like Facebook, Instagram, and WhatsApp.",
    disableBeacon: true,
  },
  {
    target: "#tour-settings-canned",
    title: "Canned Responses",
    content:
      "Create and manage pre-written responses to reply to customers faster.",
  },
  {
    target: "#tour-settings-labels",
    title: "Global Labels",
    content:
      "Organize your conversations with custom labels for better tracking.",
  },
  {
    target: "#tour-settings-profile",
    title: "Account Settings",
    content: "Update your personal information and account settings.",
  },
  {
    target: "#tour-settings-billing",
    title: "Billing & Plans",
    content:
      "Manage your subscription, view invoices, and update payment methods.",
  },
  {
    target: "#tour-settings-upgrade",
    title: "Premium Upgrades",
    content: "Explore other plans and upgrade your account for more features.",
  },
];
